import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { compose } from 'recompose';
import { Validator, WrappedFieldProps } from 'redux-form';
import { omit } from 'lodash';

import FormInput from '../FormInput';
import asFormField from '../asFormField';
import FormField, { IClearableProps, IFormFieldGeneralProps } from '../FormField';
import { validatePassword, validateLength, validateRequired, validateMatches } from '../../../utils/validators';

interface IPasswordInputProps extends IFormFieldGeneralProps, IClearableProps {
  validate?: Validator | Validator[];
}

interface IPasswordFieldProps extends IFormFieldGeneralProps {
  minLength?: number;
  maxLength?: number;
  matchesField?: string;
  matchFieldLabel?: string;
}

interface IPasswordFieldState {
  isVisible: boolean;
}

const MESSAGE_BASE = 'components.Form.PasswordField';
const HIDE_PASSWORD_MESSAGE = 'hidePassword';
const SHOW_PASSWORD_MESSAGE = 'showPassword';

const PasswordInput: React.FunctionComponent<IPasswordInputProps> = asFormField()(
  (props: WrappedFieldProps & IFormFieldGeneralProps) => {
    return <FormField type="password" InputComponent={FormInput} {...props} />;
  },
);

class PasswordField extends React.Component<IPasswordFieldProps & InjectedIntlProps, IPasswordFieldState> {
  public static defaultProps = {
    minLength: 3,
    maxLength: 25,
    matchFieldLabel: 'Unknown Field',
  };

  constructor(props: any) {
    super(props);

    this.state = {
      isVisible: false,
    };
  }

  public render() {
    const {
      intl: { formatMessage },
    } = this.props;
    const { isVisible } = this.state;

    const filteredProps = omit(this.props, ['minLength', 'maxLength', 'matchesField', 'matchFieldLabel']);
    const actionMessage = isVisible ? HIDE_PASSWORD_MESSAGE : SHOW_PASSWORD_MESSAGE;
    const extraActionContent = formatMessage({
      id: `${MESSAGE_BASE}.${actionMessage}`,
    });

    return (
      <PasswordInput
        {...filteredProps}
        type={isVisible ? 'text' : 'password'}
        validate={this.validate}
        extraActionContent={extraActionContent}
        extraActionNodeProps={{ onClick: this.toggleVisibility }}
      />
    );
  }

  private toggleVisibility = () => {
    this.setState((oldState: IPasswordFieldState) => {
      return {
        isVisible: !oldState.isVisible,
      };
    });
  };

  private validate: Validator = (value, allValues) => {
    const {
      intl: { formatMessage },
      label,
      required,
      minLength,
      maxLength,
      matchesField,
      matchFieldLabel,
    } = this.props;

    if (required && !validateRequired(value)) {
      return formatMessage(
        {
          id: 'utils.validators.shared.required',
        },
        {
          label,
        },
      );
    }

    if (value && !validatePassword(value)) {
      return formatMessage(
        {
          id: 'utils.validators.validatePassword.invalidFormat',
        },
        {
          label,
        },
      );
    }

    // @ts-ignore
    if (!validateLength(value, minLength, maxLength)) {
      return formatMessage(
        {
          id: 'utils.validators.shared.lengthRange',
        },
        {
          label,
          minLength,
          maxLength,
        },
      );
    }

    if (matchesField && !validateMatches(value, matchesField, allValues)) {
      return formatMessage(
        {
          id: 'utils.validators.shared.matchesField',
        },
        {
          label,
          matchFieldLabel,
        },
      );
    }

    return null;
  };
}

const enhance = compose<IPasswordFieldProps & InjectedIntlProps, IPasswordFieldProps>(injectIntl);

export default enhance(PasswordField);



// WEBPACK FOOTER //
// ./src/components/Form/PasswordField/index.tsx